import Box from "@mui/material/Box";
import React, { useContext, useEffect, useMemo, useState } from "react";
import RouteBreadcrumbs from "../components/shared/RouteBreadcrumbs";
import { CustomerDropdown } from "../components/CustomerDropdown";
import { Customer } from "../components/shared/interfaces/Customer";
import { MessageToast, ToastMode } from "../components/shared/MessageToast";
import { useTranslation } from "react-i18next";
import { useCompaniesByCustomerId } from "../components/data/useCompaniesByCustomerId";
import { CompanyGrid } from "../components/settings/CompanyGrid";
import { AuthContext } from "../components/contexts/AuthContext";
import { CompanyCreate } from "../components/settings/CompanyCreate";
import { Snackbar } from "../components/shared/Snackbar";
import { useAlternativeRegionNames } from "../components/data/Settings/useAlternativeRegionNames";

export function Subsets() {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();

  const altRegionNames = useAlternativeRegionNames(selectedCustomer?.id);

  const { refetch: refetchCompanies, ...companies } = useCompaniesByCustomerId(
    selectedCustomer && selectedCustomer?.id,
  );

  const [companyCreatePanelOpen, setCompanyCreatePanelOpen] = useState(false);

  useEffect(() => {
    if (authContext.userIsAuthenticated) {
      refetchCompanies();
    }
  }, [authContext.userIsAuthenticated, refetchCompanies]);

  const errorToast = useMemo(() => {
    if (companies.error) {
      return {
        show: true,
        mode: ToastMode.Error,
        text: t("common.dataFetchError", {
          val: altRegionNames.data?.companiesAltName ?? "$t(settings.companies)",
        }),
      };
    } else {
      return { show: false, mode: ToastMode.Error, text: "" };
    }
  }, [t, companies.error, altRegionNames.data?.companiesAltName]);

  function handleCustomerSelect(customer?: Customer) {
    setSelectedCustomer(customer);
  }

  function handleNewCompanyButtonClick() {
    setCompanyCreatePanelOpen(true);
  }

  function handleNewCompanyPanelClose() {
    setCompanyCreatePanelOpen(false);
  }

  function handleCompanyCreated(companyId: number, companyName: string) {
    refetchCompanies();
    setSnackbarText(
      t("common.dataSuccessfullyCreated", {
        entity: altRegionNames.data?.companyAltName ?? "$t(common.company)",
        id: companyName || companyId,
      }),
    );
    setShowSnackbar(true);
    setCompanyCreatePanelOpen(false);
  }

  return (
    <React.Fragment>
      <Box marginBottom={2}>
        <RouteBreadcrumbs />
      </Box>

      <CustomerDropdown
        selectedCustomer={selectedCustomer}
        onCustomerSelect={handleCustomerSelect}
        onSingleCustomerAvailable={(customer) => setSelectedCustomer(customer)}
        showLabel
      />

      {errorToast.show ? (
        <div className="w-100 d-flex justify-content-center pt-2">
          <MessageToast text={errorToast.text} mode={errorToast.mode} show={errorToast.show}></MessageToast>
        </div>
      ) : (
        selectedCustomer && (
          <Box marginTop={2}>
            <CompanyGrid
              customerSelected={selectedCustomer != null}
              customerId={selectedCustomer?.id}
              companiesData={companies.data}
              companiesDataLoading={companies.isLoading}
              companiesDataFetching={companies.isFetching}
              onDataRefreshClick={refetchCompanies}
              onNewCompanyClick={handleNewCompanyButtonClick}
              refreshCompanyData={refetchCompanies}
            />

            {companyCreatePanelOpen && companies.data && (
              <CompanyCreate
                open={companyCreatePanelOpen}
                onClose={handleNewCompanyPanelClose}
                customerId={selectedCustomer.id}
                onCompanyCreated={handleCompanyCreated}
                existingCompanies={companies.data}
                refreshCompanyData={refetchCompanies}
              />
            )}
          </Box>
        )
      )}

      <Snackbar
        open={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
        }}
        message={snackbarText}
      />
    </React.Fragment>
  );
}
